// Single entry point for env-derived config — read import.meta.env.{WI,RPM}_* HERE ONLY.
// WI_* = Withings platform-wide, RPM_* = Neo RPM-specific.
// `local` is reserved by Vite (.local suffix collision) — local dev runs `vite` no-mode (MODE='development').
// logic/spruce.ts must stay import-free: it is imported here, at config load.
import { spruceOrgId } from '../logic/spruce';

type EnvName = 'local' | 'dev' | 'beta' | 'prod';

export type AppConfig = {
  ENV: EnvName;
  COOKIE_NAME: string;
  COOKIE_DOMAIN: string;
  SERVICE_URL: string;
  ACCOUNT_URL: string;
  STATIC_URL: string;
  PROGRAM_ID: number;
  /** Dropshipment device-catalog key. Distinct from PROGRAM_ID (same value
   *  across beta/prod even though PROGRAM_ID differs) — the device catalog
   *  is keyed by cohort, not program. */
  COHORT_ID: number;
  /** 0 = silent, 1 = info, 2 = debug. */
  VERBOSE: number;
  /** The clinic's Spruce organization: the Call button opens Spruce when set, Twilio otherwise.
   *  `RPM_SPRUCE_ORG_ID=off` (or anything that is not an `entity_…` id) → null. */
  SPRUCE_ORG_ID: string | null;
};

function pickEnvName(): EnvName {
  const mode = import.meta.env.MODE;
  if (mode === 'dev' || mode === 'beta' || mode === 'prod') return mode;
  return 'local';
}

// Throws at module load if missing — better than malformed URLs at runtime.
// Test mode (vitest MODE='test') returns a sentinel so tests can import without env config.
function readRequiredEnv(name: string): string {
  const value = import.meta.env[name] as string | undefined;
  if (value) return value;
  if (import.meta.env.MODE === 'test') return `test://${name}`;
  throw new Error(
    `[constants] Required env var ${name} is missing. Add it to env/.env.local `
    + '(see env/.env.local.example) or to the matching env/.env.<mode> for CI builds.',
  );
}

// Numeric variant. `testFallback` is a genuine default for tunables like
// RPM_VERBOSE (not a per-env id) — pass it ONLY for those. For env-varying ids
// (RPM_PROGRAM_ID, RPM_COHORT_ID) pass no fallback: they're provided by every
// env file incl. .env.test, so a missing one throws loud rather than silently
// resolving to a hardcoded id that would be wrong in another env.
function readRequiredNumberEnv(name: string, testFallback?: number): number {
  const value = import.meta.env[name] as string | undefined;
  if (value) return Number(value);
  if (import.meta.env.MODE === 'test' && testFallback !== undefined) return testFallback;
  throw new Error(`[constants] Required env var ${name} is missing.`);
}

function resolveConfig(): AppConfig {
  const envName = pickEnvName();

  // Always direct — corp dev vhosts are CORS-allowlisted by clinical-scalews[beta],
  // so no dev proxy in any env. See .claude/rules/app.md "Local dev via vhost".
  const SERVICE_URL = readRequiredEnv('WI_SERVICE_URL');

  return {
    ENV: envName,
    COOKIE_NAME: readRequiredEnv('WI_COOKIE_NAME'),
    COOKIE_DOMAIN: readRequiredEnv('WI_COOKIE_DOMAIN'),
    SERVICE_URL,
    ACCOUNT_URL: readRequiredEnv('WI_ACCOUNT_URL'),
    STATIC_URL: readRequiredEnv('WI_STATIC_URL'),
    PROGRAM_ID: readRequiredNumberEnv('RPM_PROGRAM_ID'),
    COHORT_ID: readRequiredNumberEnv('RPM_COHORT_ID'),
    VERBOSE: readRequiredNumberEnv('RPM_VERBOSE', 0),
    SPRUCE_ORG_ID: spruceOrgId(readRequiredEnv('RPM_SPRUCE_ORG_ID')),
  };
}

export const Constants: AppConfig = resolveConfig();

// ────────────────────────────────────────────────────────────────────────────
// Withings auth-flow constants. Static (not env-overrideable) — they describe
// the Withings account portal contract that's stable across deployments.
// ────────────────────────────────────────────────────────────────────────────

/** App identifier sent on every Withings webservice call. */
export const APPNAME = 'wrpm';

/**
 * Account portal workflow + block segments — they describe the URL shape
 * `${ACCOUNT_URL}${workflow}/${block}?...`. We align with @platform/wiservice's
 * defaults (`new_workflow` / `login`) so the auto-redirect on auth errors and
 * the manual `redirectToAccount()` helper land on the same portal page.
 */
export const APPFSM_WORKFLOW_NEW = 'new_workflow';
export const APPFSM_BLOCK_LOGIN = 'login';
export const APPFSM_LOGOUT = 'logout';

/**
 * Account portal login-flow version (v2 is current). Carried in the
 * redirect-back URL as `?logver=N&...` so the portal knows which flavour to render.
 */
export const LOGIN_FLOW_VERSION_NEW = 2;

/**
 * Session keep-alive. apiv2 owns the session lifetime and returns the TTL it
 * granted in the `session.extend` body — the schedule is derived from THAT
 * (see logic/sessionKeepAlive.ts), never from a client-side assumption.
 *
 * Two apiv2 facts shape the constants below (Session_Extend.php / Cohort.php):
 *   - an RPM coordinator gets `inactive_time_log_off` (cohort default 30 min)
 *     + RPM_MIN_TIME_BETWEEN_EXTEND_SESSION (120 s) ≈ 32 min, NOT an hour;
 *   - an extend sent WITHOUT a `ttl` param floors at SESSION_DURATION_MIN
 *     (300 s = 5 min). So we always ask, and let the server clamp down.
 */
export const SESSION_EXTEND_REQUESTED_TTL_SECONDS = 3600;
/** Safety margin — extend this long before the granted TTL runs out. */
export const SESSION_EXTEND_LEAD_MS = 5 * 60 * 1000;
/** Floor between two extends (apiv2 refuses them closer than 120 s). */
export const SESSION_EXTEND_MIN_DELAY_MS = 3 * 60 * 1000;
/** Only when the server returned no usable `ttl` — deliberately conservative. */
export const SESSION_EXTEND_FALLBACK_MS = 10 * 60 * 1000;

/**
 * Email domains we treat as Withings staff (used for the `isWithinger` flag
 * surfaced in the UI for badging or perms — informational, not security).
 */
export const WITHINGS_EMAILS = [
  '@withings.com',
  '@corp.withings.com',
  '@ext.withings.com',
];

/**
 * wiservice status codes. The @platform/wiservice package handles 103/239/
 * 250/501 by auto-redirecting to the account portal — we never have to check
 * these client-side except for the `sessionIsVerified` callback path where we
 * intercept to make a per-host (localhost vs vhost) decision before redirect.
 */
export const WS_STATUS_OK = 0;
export const WS_STATUS_AUTHFAILED_BLANKING_PASSWORD = 103;
export const WS_STATUS_UNVERIFIED_SESSION = 239;
export const WS_STATUS_WRONG_SESSIONID = 250;
export const WS_STATUS_INVALID_SESSION = 501;
export const AUTH_ERROR_CODES = [
  WS_STATUS_AUTHFAILED_BLANKING_PASSWORD,
  WS_STATUS_UNVERIFIED_SESSION,
  WS_STATUS_WRONG_SESSIONID,
  WS_STATUS_INVALID_SESSION,
] as const;
